/**
 * Ежедневная выгрузка копии на Яндекс.Диск.
 *
 * Здесь протокол (`yandex-disk.ts`) встречается с настройками человека и с
 * ежедневной копией. Правило простое: копия, уже записанная на телефон,
 * следом уходит на Диск — если человек это подключил и не выключил.
 *
 * ## Почему выгрузка не роняет копию
 *
 * Файл на телефоне важнее выгрузки: он есть даже без сети. Поэтому сбой Диска
 * не отменяет копию и не мешает запуску приложения — он лишь записывается,
 * чтобы карточка могла честно сказать «последняя выгрузка не удалась». Молчать
 * об этом нельзя: человек, уверенный, что копии уходят в облако, узнал бы
 * правду в худший момент.
 *
 * ## Про пароль
 *
 * Хранится пароль приложения — тот, что заводится на id.yandex.ru отдельно от
 * пароля аккаунта. Его можно отозвать одним нажатием, он даёт доступ только к
 * Диску и не пускает в почту.
 *
 * Лежит он отдельно от остальных настроек: на телефоне — в Keychain или
 * Keystore, где ключ защищён системой, в браузере — в базе приложения, потому
 * что другого места там нет. Соседство пароля с задачами в одной таблице было
 * ошибкой: базу читает всё, что имеет доступ к устройству.
 */

import { db } from '@/shared/db/database';
import { readSecret, removeSecret, writeSecret } from '@/shared/lib/secret-storage';
import { type IsoDate, today as todayIso } from '@/shared/lib/datetime';

import { serializeBackup } from './backup-payload';
import { backupFileName, type Backup } from './export-data';
import {
  checkDiskAccess,
  davRequest,
  type DavRequest,
  type DiskCheck,
  type DiskCredentials,
  downloadDiskBackup,
  isDiskUploadSupported,
  listDiskBackups,
  pruneDiskBackups,
  uploadBackupToDisk,
} from './yandex-disk';

export interface DiskSettings {
  login: string;
  /** Пароль приложения. Хранится не здесь — см. `secret-storage`. */
  password: string;
  /** Выгружать ли. Отключается, не теряя пароль. */
  enabled: boolean;
  /** День последней удачной выгрузки. */
  lastUploadDate: IsoDate | null;
  /** Что пошло не так в последний раз. `null` — всё удалось. */
  lastError: string | null;
}

const EMPTY: DiskSettings = {
  login: '',
  password: '',
  enabled: false,
  lastUploadDate: null,
  lastError: null,
};

const diskKey = (userId: string) => `backup.disk:${userId}`;

const diskPasswordKey = (userId: string) => `backup.disk.password:${userId}`;

export async function readDiskSettings(userId: string): Promise<DiskSettings> {
  const row = await db.settings.get(diskKey(userId));
  const value = row?.value as Partial<DiskSettings> | undefined;

  /*
    Пароль приходит из защищённого хранилища, а не из настроек. Если он ещё
    лежит рядом с ними — от прежних версий, — он переезжает при первом же
    сохранении: `writeDiskSettings` кладёт его на новое место и вычищает
    старое.
  */
  const secret = await readSecret(diskPasswordKey(userId));

  return {
    login: value?.login ?? EMPTY.login,
    password: secret ?? value?.password ?? EMPTY.password,
    enabled: value?.enabled ?? EMPTY.enabled,
    lastUploadDate: value?.lastUploadDate ?? EMPTY.lastUploadDate,
    lastError: value?.lastError ?? EMPTY.lastError,
  };
}

export async function writeDiskSettings(
  userId: string,
  patch: Partial<DiskSettings>,
): Promise<DiskSettings> {
  const next = { ...(await readDiskSettings(userId)), ...patch };

  if (next.password !== '') await writeSecret(diskPasswordKey(userId), next.password);

  // В настройках пароля не остаётся: рядом с ними лежат задачи и здоровье, и
  // читает их всё, что дотянулось до базы.
  const { password: _password, ...stored } = next;
  await db.settings.put({ key: diskKey(userId), value: stored, updatedAt: Date.now() });

  return next;
}

/** Забывает Диск целиком — вместе с паролем. */
export async function forgetDisk(userId: string): Promise<void> {
  await db.settings.delete(diskKey(userId));
  await removeSecret(diskPasswordKey(userId));
}

/** Диск подключён: есть логин с паролем и выгрузка не выключена. */
export function isDiskConnected(settings: DiskSettings): boolean {
  return settings.login !== '' && settings.password !== '';
}

/**
 * Проверяет пару логин-пароль и запоминает её, если Диск ответил.
 *
 * Пароль сохраняется только после удачной проверки: запомнить неверный —
 * значит каждый день молча не выгружать копию.
 */
export async function connectDisk(
  userId: string,
  credentials: DiskCredentials,
  request: DavRequest = davRequest(credentials),
): Promise<DiskCheck> {
  const result = await checkDiskAccess(request);
  if (result !== 'ok') return result;

  await writeDiskSettings(userId, {
    login: credentials.login,
    password: credentials.password,
    enabled: true,
    lastError: null,
  });

  return 'ok';
}

export interface DiskUploadResult {
  uploaded: boolean;
  /** Сколько лишних копий убрано с Диска. */
  pruned: number;
  error: string | null;
}

/**
 * Выгружает копию на Диск.
 *
 * Возвращает `null`, когда выгружать некуда или незачем: Диск не подключён,
 * выгрузка выключена, устройство её не умеет или копия за этот день уже там.
 * Это не сбой, а обычное состояние.
 */
export async function uploadBackupForDay(
  userId: string,
  backup: Backup,
  options: { today?: IsoDate; request?: DavRequest; force?: boolean; content?: string } = {},
): Promise<DiskUploadResult | null> {
  const today = options.today ?? todayIso();
  const settings = await readDiskSettings(userId);

  if (!isDiskConnected(settings) || !settings.enabled) return null;
  if (!options.request && !isDiskUploadSupported()) return null;
  if (!options.force && settings.lastUploadDate === today) return null;

  const request = options.request ?? davRequest(settings);

  try {
    // Текст мог быть подготовлен раньше — тогда он уже закрыт паролем.
    const content = options.content ?? (await serializeBackup(userId, backup));
    await uploadBackupToDisk(request, backupFileName(backup.exportedAt), content);
  } catch (error) {
    /*
      Сбой выгрузки не отменяет копию на телефоне — она уже записана. Но и
      молчать о нём нельзя: человек, уверенный, что копии уходят в облако,
      узнал бы правду в худший момент.
    */
    const message = error instanceof Error ? error.message : 'Не удалось выгрузить';
    await writeDiskSettings(userId, { lastError: message });

    return { uploaded: false, pruned: 0, error: message };
  }

  // Прореживание — дело десятое: копия уже на Диске, и неудача здесь ничего
  // не отменяет.
  let pruned = 0;
  try {
    pruned = await pruneDiskBackups(request);
  } catch {
    // Место на Диске подождёт до следующего раза.
  }

  await writeDiskSettings(userId, { lastUploadDate: today, lastError: null });
  return { uploaded: true, pruned, error: null };
}

/** Копия на Диске — то, что человек выбирает для восстановления. */
export interface DiskBackupFile {
  name: string;
  date: IsoDate;
  size: number;
}

/**
 * Копии, лежащие на Диске.
 *
 * Отдельно от выгрузки: посмотреть, что там есть, человек хочет и тогда, когда
 * ежедневная выгрузка выключена, — например, перенося данные из браузера в
 * приложение.
 */
export async function listBackupsOnDisk(
  userId: string,
  request?: DavRequest,
): Promise<DiskBackupFile[]> {
  const settings = await readDiskSettings(userId);
  if (!isDiskConnected(settings)) return [];

  const files = await listDiskBackups(request ?? davRequest(settings));
  return files.map((file) => ({ name: file.name, date: file.date, size: file.size }));
}

/**
 * Забирает копию с Диска.
 *
 * Путь переноса данных, которому не нужен ни выбор файла, ни доступ к
 * хранилищу телефона: копия из браузера уходит на Диск, приложение забирает её
 * оттуда. Возвращается текст как есть — он может быть закрыт паролем, и
 * спрашивать его должна общая шторка, а не этот модуль.
 */
export async function readBackupFromDisk(
  userId: string,
  name: string,
  request?: DavRequest,
): Promise<string> {
  const settings = await readDiskSettings(userId);
  if (!isDiskConnected(settings)) throw new Error('Диск не подключён');

  return downloadDiskBackup(request ?? davRequest(settings), name);
}
